import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import * as yup from "yup";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import { PASSWORD_REGEX } from "../../utils/ValidationRules";
import { toast } from "../../utils/toast";
import { useAuthStore } from "../../stores/auth.store";

type Inputs = {
	password: string;
	passwordConfirmation: string;
};

type Match = {
	params: Params;
};

type Params = {
	token: string;
};

const validationSchema = yup.object().shape({
	password: yup.string().required("PASSWORD_REQUIRED").matches(PASSWORD_REGEX, {
		message: "PASSWORD_VALIDATION_MESSAGE",
		excludeEmptyString: true,
	}),
	passwordConfirmation: yup
		.string()
		.required("PASSWORD_REQUIRED")
		.oneOf([yup.ref("password")], "PASSWORD_MISMATCH"),
});

const ResetPassword: React.FC = () => {
	const [success, setSuccess] = useState(false);
	const authStore = useAuthStore();

	const { t } = useTranslation();

	const { token } = useParams();

	const [tokenIsValid, setTokenValidation] = useState<boolean>(true);

	const { register, handleSubmit, formState } = useForm<Inputs>({
		mode: "onBlur",
		resolver: yupResolver(validationSchema),
	});

	const onSubmit = handleSubmit(async (e) => {
		const results = await authStore.passwordReset(e.password, token!);
		if (results) {
			toast.success(FEEDBACK_MESSAGE_TYPE.RESET_PASSWORD_SUCCESS);
			setSuccess(true);
		} else toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
	});

	useEffect(() => {
		const checkValidation = async () => {
			try {
				const result = await authStore.validatePaswordResetToken(token!);
				setTokenValidation(result.data.tokenIsValid);
			} catch (error) {
				setTokenValidation(false);
			}
		};
		checkValidation();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	if (success) {
		return (
			<FormContainer>
				<AppText margin="2rem" size="l" textAlign="center">
					Återställningen lyckades, du kan nu logga in med ditt nya lösenord.
				</AppText>
			</FormContainer>
		);
	}

	return (
		<FormContainer>
			{!tokenIsValid && (
				<AppText margin="1rem" color="danger" size="m">
					{t("INVALID_TOKEN")}
				</AppText>
			)}
			<AppText
				margin="2rem"
				letterSpacing="0.24rem"
				color="grayText"
				size="l"
				textTransform="uppercase"
			>
				{t("RESET_PASSWORD")}
			</AppText>
			<FormComponent onSubmit={onSubmit}>
				<AppInput
					{...register("password")}
					placeholder="PASSWORD"
					icon="password"
					type="password"
					errors={formState.errors?.password?.message}
				/>
				<AppInput
					{...register("passwordConfirmation")}
					placeholder="pwReset.confirmPassword"
					icon="password"
					type="password"
					errors={formState.errors?.passwordConfirmation?.message}
				/>
				<Button margin="2rem" disabled={!tokenIsValid}>
					{t("SEND")}
				</Button>
			</FormComponent>
		</FormContainer>
	);
};

export default ResetPassword;

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.theme.colors.COLOR_DARK_GRAY};
	box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.1);
	border-radius: 5px;
	width: 25.625rem;
	height: 25.3125rem;
	justify-content: center;
	margin-bottom: 5vh; /* to push login box up slightly above vertical center */
`;

const FormComponent = styled.form`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 270px;
`;
